import {
    MetricPerformanceData,
    MetricTooltipData,
    ThresholdData,
} from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { KpiType } from '../../coaching-insight/insight-widget/model/kpi-type.model';
import { sdpNumber } from '../../common/pipe/single-decimal-point';
import { epicDependencies } from '../../store';
import { KpiTypeTranslationMap } from '../constants/kpi-type-translation-map';
import { CardItem } from '../models/card-item.model';
import { RowType } from '../models/row-type.enum';
import { StagingKpiThreshold } from '../models/staging-kpi-threshold.model';
import { WpCoachingCard } from '../models/wp-coaching-card.model';
import { TranslationKeyMap } from '../my-coaching-card.i18n';

import { calculateTrendValues } from './get-card-row-data.function';
import { getHeaderRowData } from './get-header-row-data.function';
import { getProgressDataWinningPct } from './get-progress-data-winning-pct.function';

function getCardType(kpiTypeId: number): RowType {
    return kpiTypeId === KpiType.FL_WINNING_PERCENTAGE
        ? RowType.FLWP
        : RowType.WP;
}

function createProgressBarItem(
    titleKey: string,
    value: number | null,
    formattedValue: string | null,
    translateService: TranslateService,
): MetricTooltipData {
    const title = translateService.instant(titleKey);
    const tooltipText = `${String(title)}: ${formattedValue ?? ''}`;

    return {
        title: String(title),
        value,
        tooltipText,
    };
}

export function getWinningPctRowData(
    wpCoachingCard: WpCoachingCard | null,
    coachingCardThreshold: StagingKpiThreshold[],
    kpiTypeId: number,
    superGroupScore: number,
): MetricPerformanceData {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);

    const trendValues = calculateTrendValues(
        wpCoachingCard?.winning * 100,
        wpCoachingCard?.prevWinning * 100,
        wpCoachingCard?.winningThresholdColour,
        translateService,
    );

    const thresholds: ThresholdData[] = getProgressDataWinningPct(
        wpCoachingCard,
        coachingCardThreshold,
    );

    const noDataMessage =
        wpCoachingCard == null
            ? translateService.instant(
                  kpiTypeId === KpiType.FL_WINNING_PERCENTAGE
                      ? TranslationKeyMap._11166_NO_FRONT_LINE_WINNING__DATA_AVAILABLE
                      : TranslationKeyMap._10102_NO_WINNING__DATA_AVAILABLE,
              )
            : '';

    const noActualDataMessage =
        wpCoachingCard?.winning == null
            ? translateService.instant(
                  TranslationKeyMap._11173_NO_TARGET_THRESHOLDS_CONFIGURED,
              )
            : null;

    const noTargetDataMessage =
        wpCoachingCard?.winningTarget == null
            ? translateService.instant(
                  TranslationKeyMap._11173_NO_TARGET_THRESHOLDS_CONFIGURED,
              )
            : null;
    const metricNameTooltip = `${String(
        translateService.instant(TranslationKeyMap._11177_LAUNCH),
    )} ${String(translateService.instant(KpiTypeTranslationMap[kpiTypeId]))}`;

    return {
        metricName: translateService.instant(KpiTypeTranslationMap[kpiTypeId]),
        metricNameTooltip,
        trendValues,
        progressBarData: {
            thresholds,
            actualValue: createProgressBarItem(
                TranslationKeyMap._725_ACTUAL,
                Math.round((wpCoachingCard?.winning ?? 0) * 100),
                sdpNumber((wpCoachingCard?.winning ?? 0) * 100),
                translateService,
            ),
            targetValue: createProgressBarItem(
                TranslationKeyMap._726_TARGET,
                (wpCoachingCard?.winningTarget ?? 0) * 100,
                sdpNumber((wpCoachingCard?.winningTarget ?? 0) * 100),
                translateService,
            ),
            superGroupValue: createProgressBarItem(
                TranslationKeyMap._67_SUPERGROUP,
                (superGroupScore ?? 0) * 100,
                sdpNumber((superGroupScore ?? 0) * 100),
                translateService,
            ),
            minValue: 0,
            maxValue: 100,
        },
        color: wpCoachingCard?.winningThresholdColour ?? '#000',
        noActualDataMessage,
        noTargetDataMessage,
        noDataMessage,
        isVisible: true,
        isExpandable: false,
        rowType: getCardType(kpiTypeId),
    };
}

export function getWinningPctCardItem(
    wpCoachingCard: WpCoachingCard | null,
    coachingCardThreshold: StagingKpiThreshold[],
    kpiTypeId: number,
    superGroupScore: number,
): CardItem {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);

    const { startDateDataRange, endDateDataRange } = wpCoachingCard ?? {};
    const headerData = getHeaderRowData(
        startDateDataRange,
        endDateDataRange,
        kpiTypeId,
    );

    if (wpCoachingCard == null || Object.keys(wpCoachingCard).length === 0) {
        const noDataMessage = translateService.instant(
            kpiTypeId === KpiType.FL_WINNING_PERCENTAGE
                ? TranslationKeyMap._11166_NO_FRONT_LINE_WINNING__DATA_AVAILABLE
                : TranslationKeyMap._10102_NO_WINNING__DATA_AVAILABLE,
        );
        const metricNameTooltip = `${String(
            translateService.instant(TranslationKeyMap._11177_LAUNCH),
        )} ${String(
            translateService.instant(KpiTypeTranslationMap[kpiTypeId]),
        )}`;
        return {
            headerData,
            cardRowsData: [
                {
                    metricPerformanceData: {
                        metricName: translateService.instant(
                            KpiTypeTranslationMap[kpiTypeId],
                        ),
                        metricNameTooltip,
                        trendValues: null,
                        progressBarData: null,
                        color: '#000000',
                        noActualDataMessage: null,
                        noTargetDataMessage: null,
                        noDataMessage,
                        isVisible: false,
                        isExpandable: false,
                    },
                },
            ],
            type: getCardType(kpiTypeId),
        };
    }

    const rowData: MetricPerformanceData = getWinningPctRowData(
        wpCoachingCard,
        coachingCardThreshold,
        kpiTypeId,
        superGroupScore,
    );

    return {
        headerData,
        cardRowsData: [
            {
                metricPerformanceData: rowData,
            },
        ],
        type: getCardType(kpiTypeId),
    };
}
